import { NgModule } from '@angular/core';
import {
  DefaultUrlSerializer,
  RouterModule,
  Routes,
  UrlSerializer,
  UrlTree,
} from '@angular/router';
import { ApiReferenceLayoutComponent } from './layout/api-reference-layout/api-reference-layout.component';
import { DashboardLayoutComponent } from './layout/dashboard-layout/dashboard-layout.component';
import { LandingLayoutComponent } from './layout/landing-layout/landing-layout.component';
import { LoginLayoutComponent } from './layout/login-layout/login-layout.component';
import { LandingPageComponent } from './pages/landing-page/landing-page.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { RegistrationPageComponent } from './pages/registration-page/registration-page.component';
import { ManagementPageComponent } from './pages/management-page/management-page.component';
import { AuthGuard } from './guards/auth.guard';
import { RoleGuard } from './guards/role.guard';
import { ProfilePageComponent } from './pages/profile-page/profile-page.component';
import { FixApiComponent } from './components/stoplight-apis/fix-api/fix-api.component';
import { ExtrenalApiComponent } from './components/stoplight-apis/extrenal-api/extrenal-api.component';
import { FeedApiComponent } from './components/stoplight-apis/feed-api/feed-api.component';
import { PendingRequestComponent } from './pages/pending-request-page/pending-request-page.component';
import { ChangePasswordPageComponent } from './pages/change-password-page/change-password-page.component';
import { ForgotPasswordPageComponent } from './pages/forgot-password-page/forgot-password-page.component';
import { RestApiComponent } from './components/stoplight-apis/rest-api/rest-api.component';
import { ApiGuard } from './guards/api.guard';
import { ConfigurationsPageMainComponent } from './pages/configurations-page-main/configurations-page-main.component';
import { MonitoringDashboardComponent } from './pages/monitoring-dashboard/monitoring-dashboard.component';
import { MicroInvestingApiComponent } from './components/stoplight-apis/micro-investing-api/micro-investing-api.component';
import { InvestApiComponent } from './components/stoplight-apis/invest-api/invest-api.component';
import { MaintenanceSchedulingComponent } from './pages/maintenance-scheduling/maintenance-scheduling.component';
import { GeneratePresignedUrlComponent } from './pages/generate-presigned-url/generate-presigned-url.component';
import { InstConfigsComponent } from './pages/inst-configs/inst-configs.component';
import { KeyGenAppLayoutComponent } from './layout/key-gen-app-layout/key-gen-app-layout.component';
import { GenerateKeysComponent } from './pages/generate-keys/generate-keys.component';
import { GenerateAssertionComponent } from './pages/generate-assertion/generate-assertion.component';
import { AssertionGenAppLayoutComponent } from './layout/assertion-gen-app-layout/assertion-gen-app-layout.component';
import { Base64EncoderComponent } from './pages/base64-encoder/base64-encoder.component';
import { Base64EncoderAppLayoutComponent } from './layout/base64-encoder-app-layout/base64-encoder-app-layout.component';
import { UploadZipComponent } from './pages/upload-zip/upload-zip.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';
import { ForbiddenPageComponent } from './pages/forbidden-page/forbidden-page.component';

const routes: Routes = [
  {
    path: '',
    component: LandingLayoutComponent,
    children: [{ path: '', component: LandingPageComponent }],
  },
  {
    path: 'feed-api-reference',
    component: ApiReferenceLayoutComponent,
    children: [{ path: '**', component: FeedApiComponent }],
    canActivate: [AuthGuard, ApiGuard],
  },

  // Bare /doc has no site to show: take the visitor back to the landing page
  { path: 'doc', redirectTo: '/', pathMatch: 'full' },
  { path: 'docs', redirectTo: '/', pathMatch: 'full' },

  // trade api related routes (rest-api-reference is legacy alias for doc/trade)
  ...[
    'rest-api-reference',
    'doc/trade',
    'updates/trade',
    'status/trade',
    'playground/trade',
  ].map((path) => ({
    path,
    component: ApiReferenceLayoutComponent,
    // Guard the parent too: a refresh/deep-link must be checked before the
    // iframe component mounts (the child guard alone misses the entry point)
    canActivate: [AuthGuard, ApiGuard],
    children: [
      {
        path: '**',
        component: RestApiComponent,
        canActivate: [AuthGuard, ApiGuard],
      },
    ],
  })),

  {
    path: 'fix-api-reference',
    component: ApiReferenceLayoutComponent,
    children: [{ path: '**', component: FixApiComponent }],
    canActivate: [AuthGuard, ApiGuard],
  },
  {
    path: 'external-api-reference',
    component: ApiReferenceLayoutComponent,
    children: [{ path: '**', component: ExtrenalApiComponent }],
    canActivate: [AuthGuard, ApiGuard],
  },
  {
    path: 'doc/microinvest',
    component: ApiReferenceLayoutComponent,
    canActivate: [AuthGuard, ApiGuard],
    children: [
      {
        path: '**',
        component: MicroInvestingApiComponent,
        canActivate: [AuthGuard, ApiGuard],
      },
    ],
  },
  {
    path: 'doc/invest',
    component: ApiReferenceLayoutComponent,
    canActivate: [AuthGuard, ApiGuard],
    children: [
      {
        path: '**',
        component: InvestApiComponent,
        canActivate: [AuthGuard, ApiGuard],
      },
    ],
  },
  {
    path: 'registration',
    component: LoginLayoutComponent,
    children: [{ path: '', component: RegistrationPageComponent }],
    canActivate: [AuthGuard],
  },
  {
    path: 'password-reset',
    component: LoginLayoutComponent,
    children: [{ path: '', component: ForgotPasswordPageComponent }],
    canActivate: [AuthGuard],
  },
  {
    path: 'login',
    component: LoginLayoutComponent,
    children: [
      { path: 'user-login', component: LoginPageComponent },
      { path: 'user-login/:status', component: LoginPageComponent },
      { path: 'change-password', component: ChangePasswordPageComponent },
    ],
    canActivate: [AuthGuard],
  },
  {
    path: 'dashboard',
    component: DashboardLayoutComponent,
    children: [
      { path: 'registration', component: RegistrationPageComponent },
      { path: 'user-management', component: ManagementPageComponent },
      { path: 'user-profile', component: ProfilePageComponent },
      { path: 'user-pending-request', component: PendingRequestComponent },
      { path: 'change-password', component: ChangePasswordPageComponent },
      { path: 'configurations', component: ConfigurationsPageMainComponent },
      { path: 'monitoring-dashboard', component: MonitoringDashboardComponent },
      {
        path: 'maintenance-scheduling',
        component: MaintenanceSchedulingComponent,
        canActivate: [RoleGuard],
        data: { expectedRole: ['admin'] },
      },
      {
        path: 'generate-presigned-url',
        component: GeneratePresignedUrlComponent,
      },
      { path: 'institution-app-data', component: InstConfigsComponent },
      { path: 'inst-configs', component: InstConfigsComponent },
      {
        path: 'upload-confluence-document',
        component: UploadZipComponent,
        canActivate: [RoleGuard],
        data: { expectedRole: ['admin'] },
      },
    ],
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRole: [
        'admin',
        'broker',
        'brokerAdmin',
        'internalUser',
        'sysAdmin',
      ],
    },
  },
  {
    path: 'generate-keys',
    component: KeyGenAppLayoutComponent,
    children: [
      {
        path: '**',
        component: GenerateKeysComponent,
        canActivate: [],
      },
    ],
  },
  {
    path: 'generate-assertion',
    component: AssertionGenAppLayoutComponent,
    children: [
      {
        path: '**',
        component: GenerateAssertionComponent,
        canActivate: [],
      },
    ],
  },
  {
    path: 'base64-encode',
    component: Base64EncoderAppLayoutComponent,
    children: [
      {
        path: '**',
        component: Base64EncoderComponent,
        canActivate: [],
      },
    ],
  },
  { path: '404', component: NotFoundPageComponent },
  { path: '403', component: ForbiddenPageComponent },
  // Legacy /docs path: redirect to landing page instead of 404
  { path: 'docs', redirectTo: '/', pathMatch: 'full' },
  // Wildcard — must be last: catches any path not matched above
  { path: '**', redirectTo: '/404' },
];

/**
 * Angular's DefaultUrlSerializer does not ignore trailing slashes, so "/doc/"
 * fails to match `{ path: 'doc', pathMatch: 'full' }` and falls through to the
 * 404 wildcard. Strip trailing slashes (keeping query/hash) before parsing so
 * "/doc" and "/doc/" behave identically.
 */
export class TrailingSlashUrlSerializer extends DefaultUrlSerializer {
  override parse(url: string): UrlTree {
    return super.parse(TrailingSlashUrlSerializer.stripTrailingSlash(url));
  }

  private static stripTrailingSlash(url: string): string {
    const splitIdx = url.search(/[?#]/);
    const path = splitIdx === -1 ? url : url.slice(0, splitIdx);
    const rest = splitIdx === -1 ? '' : url.slice(splitIdx);
    const cleaned = path.length > 1 ? path.replace(/\/+$/, '') : path;
    return (cleaned || '/') + rest;
  }
}

@NgModule({
  imports: [RouterModule.forRoot(routes, { enableTracing: false })],
  exports: [RouterModule],
  providers: [
    { provide: UrlSerializer, useClass: TrailingSlashUrlSerializer },
  ],
})
export class AppRoutingModule {}
